/**
 * Business Event Landing Page
 * Annam's Desi Community Business Event — 24 May 2026, Near Darmstadt
 *
 * Features:
 * - Professional dark/gold design
 * - English/Urdu language toggle
 * - Event details, agenda, speaker info
 * - Registration form with Stripe payment
 * - Countdown timer
 */
import { useState, useEffect, useMemo } from "react";
import { useLocation, useSearch } from "wouter";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  Calendar,
  Clock,
  MapPin,
  Users,
  ArrowRight,
  Globe,
  CheckCircle2,
  Loader2,
  Star,
  Briefcase,
  Home,
  MessageSquare,
  Handshake,
  Mic,
  Lightbulb,
  Sparkles,
  Share2,
  Link,
  Check,
} from "lucide-react";

// ─── CDN Assets ───
const ANNAM_PHOTO =
  "https://d2xsxph8kpxj0f.cloudfront.net/310519663397508885/Atzyym4VHHtDL2VhJSwxBc/annam-business-1_f8badebe.jpeg";

// ─── Translations ───
type Lang = "en" | "ur";

const t = {
  en: {
    langLabel: "اردو",
    langSwitch: "ur" as Lang,
    // Hero
    heroTag: "Exclusive Business Event",
    heroTitle: "Annam's Business Event",
    heroSubtitle:
      "An exclusive gathering for the Desi community in Germany — connect, learn, and grow together.",
    heroDate: "May 24, 2026",
    heroTime: "10:00 AM – 3:00 PM",
    heroLocation: "Near Darmstadt, Germany",
    heroCta: "Register Now",
    heroSpots: "Limited Spots Available",
    // Countdown
    countdownDays: "Days",
    countdownHours: "Hours",
    countdownMinutes: "Minutes",
    countdownSeconds: "Seconds",
    // About
    aboutTitle: "About This Event",
    aboutText:
      "Join Annam Hanfstein for an inspiring day of business talks, networking, and community building. This exclusive event brings together ambitious professionals and entrepreneurs from the Desi community in Germany. Whether you're starting a business, investing in real estate, or looking for cooperation opportunities — this is your day.",
    aboutHost: "Your Host",
    aboutHostName: "Annam Hanfstein",
    aboutHostBio:
      "Entrepreneur, Health Tech Innovator, and Community Leader. Annam bridges the gap between traditional wisdom and modern business, empowering the Desi community in Germany.",
    // Agenda
    agendaTitle: "Event Agenda",
    agendaItems: [
      {
        time: "10:00",
        title: "Welcome & Introduction",
        desc: "Meet your fellow attendees and set the tone for the day",
        icon: "users",
      },
      {
        time: "10:30",
        title: "Motivation Talk",
        desc: "Inspiring keynote on building success in Germany",
        icon: "star",
      },
      {
        time: "11:15",
        title: "Real Estate Q&A",
        desc: "Expert insights on property investment in Germany",
        icon: "home",
      },
      {
        time: "12:00",
        title: "Business Launch: Desi Warehouse",
        desc: "Exclusive pre-launch of Annam's Desi Warehouse concept",
        icon: "briefcase",
      },
      {
        time: "12:45",
        title: "Lunch Break & Networking",
        desc: "Enjoy authentic cuisine and connect with fellow attendees",
        icon: "message",
      },
      {
        time: "13:30",
        title: "Making Business in Germany — Q&A",
        desc: "Practical guidance on starting and running a business",
        icon: "lightbulb",
      },
      {
        time: "14:00",
        title: "Special Guest Talk",
        desc: "A surprise guest shares their success story",
        icon: "mic",
      },
      {
        time: "14:30",
        title: "Cooperation Possibilities",
        desc: "Explore partnerships and collaboration opportunities",
        icon: "handshake",
      },
    ],
    // Form
    formTitle: "Register for the Event",
    formSubtitle: "Secure your spot — limited availability",
    formFirstName: "First Name",
    formLastName: "Last Name",
    formEmail: "Email Address",
    formPhone: "Phone Number",
    formApplication: "Tell us about yourself — who are you and why do you want to attend?",
    formApplicationPlaceholder:
      "Share a bit about your background, your business interests, and what you hope to gain from this event...",
    formSubmit: "Register & Pay",
    formPrice: "79",
    formPriceCurrency: "EUR",
    formProcessing: "Processing...",
    formAdultsOnly: "Adults only",
    formVibe: "No bad vibes, just good talks.",
    formDisclaimer:
      "By registering, you agree to our terms. Your ticket will be sent to your email after payment.",
    // Success
    successTitle: "Registration Confirmed!",
    successText:
      "Your ticket has been sent to your email. Please save it or take a screenshot.",
    successBack: "Back to Event Page",
    // Cancelled
    cancelledTitle: "Payment Cancelled",
    cancelledText:
      "Your registration was not completed. You can try again below.",
    // Share
    shareTitle: "Share This Event",
    shareLinkedIn: "LinkedIn",
    shareTwitter: "X (Twitter)",
    shareWhatsApp: "WhatsApp",
    shareFacebook: "Facebook",
    shareCopy: "Copy Link",
    shareCopied: "Link Copied!",
    shareText: "Join Annam's exclusive Business Event for the Desi community in Germany — May 24, 2026 near Darmstadt. Connect, learn, and grow together!",
    // Footer
    footerText: "Organized by Annam Hanfstein",
    footerRights: "All rights reserved.",
  },
  ur: {
    langLabel: "English",
    langSwitch: "en" as Lang,
    heroTag: "خصوصی بزنس ایونٹ",
    heroTitle: "اننم کا بزنس ایونٹ",
    heroSubtitle:
      "جرمنی میں پاکستانی کمیونٹی کے لیے ایک خصوصی اجتماع — مل کر سیکھیں، جڑیں اور ترقی کریں۔",
    heroDate: "24 مئی 2026",
    heroTime: "صبح 10:00 — دوپہر 3:00",
    heroLocation: "ڈارمشٹاڈ کے قریب، جرمنی",
    heroCta: "ابھی رجسٹر کریں",
    heroSpots: "محدود نشستیں دستیاب ہیں",
    countdownDays: "دن",
    countdownHours: "گھنٹے",
    countdownMinutes: "منٹ",
    countdownSeconds: "سیکنڈ",
    aboutTitle: "اس ایونٹ کے بارے میں",
    aboutText:
      "اننم ہنفسٹین کے ساتھ بزنس گفتگو، نیٹ ورکنگ اور کمیونٹی بلڈنگ کا ایک متاثر کن دن۔ یہ خصوصی ایونٹ جرمنی میں پاکستانی کمیونٹی کے پرعزم پیشہ ور افراد اور کاروباری افراد کو اکٹھا کرتا ہے۔",
    aboutHost: "آپ کی میزبان",
    aboutHostName: "اننم ہنفسٹین",
    aboutHostBio:
      "کاروباری، ہیلتھ ٹیک انوویٹر، اور کمیونٹی لیڈر۔ اننم روایتی حکمت اور جدید کاروبار کے درمیان پل بناتی ہیں۔",
    agendaTitle: "ایونٹ کا شیڈول",
    agendaItems: [
      {
        time: "10:00",
        title: "خوش آمدید اور تعارف",
        desc: "ساتھی شرکاء سے ملیں اور دن کا آغاز کریں",
        icon: "users",
      },
      {
        time: "10:30",
        title: "حوصلہ افزائی کی گفتگو",
        desc: "جرمنی میں کامیابی حاصل کرنے پر متاثر کن تقریر",
        icon: "star",
      },
      {
        time: "11:15",
        title: "رئیل اسٹیٹ سوال و جواب",
        desc: "جرمنی میں جائیداد کی سرمایہ کاری پر ماہرانہ بصیرت",
        icon: "home",
      },
      {
        time: "12:00",
        title: "بزنس لانچ: دیسی ویئر ہاؤس",
        desc: "اننم کے دیسی ویئر ہاؤس کانسیپٹ کی خصوصی پری لانچ",
        icon: "briefcase",
      },
      {
        time: "12:45",
        title: "دوپہر کا کھانا اور نیٹ ورکنگ",
        desc: "مستند کھانوں سے لطف اندوز ہوں اور شرکاء سے ملیں",
        icon: "message",
      },
      {
        time: "13:30",
        title: "جرمنی میں کاروبار — سوال و جواب",
        desc: "کاروبار شروع کرنے اور چلانے پر عملی رہنمائی",
        icon: "lightbulb",
      },
      {
        time: "14:00",
        title: "خصوصی مہمان کی گفتگو",
        desc: "ایک خصوصی مہمان اپنی کامیابی کی کہانی شیئر کریں گے",
        icon: "mic",
      },
      {
        time: "14:30",
        title: "تعاون کے مواقع",
        desc: "شراکت داری اور تعاون کے مواقع تلاش کریں",
        icon: "handshake",
      },
    ],
    formTitle: "ایونٹ کے لیے رجسٹر کریں",
    formSubtitle: "اپنی نشست محفوظ کریں — محدود دستیابی",
    formFirstName: "پہلا نام",
    formLastName: "آخری نام",
    formEmail: "ای میل ایڈریس",
    formPhone: "فون نمبر",
    formApplication:
      "اپنے بارے میں بتائیں — آپ کون ہیں اور آپ کیوں شرکت کرنا چاہتے ہیں؟",
    formApplicationPlaceholder:
      "اپنے پس منظر، کاروباری دلچسپیوں، اور اس ایونٹ سے آپ کیا حاصل کرنا چاہتے ہیں کے بارے میں بتائیں...",
    formSubmit: "رجسٹر کریں اور ادائیگی کریں",
    formPrice: "79",
    formPriceCurrency: "EUR",
    formProcessing: "پروسیسنگ...",
    formAdultsOnly: "صرف بالغ افراد",
    formVibe: "کوئی بری وائبز نہیں، صرف اچھی گفتگو۔",
    formDisclaimer:
      "رجسٹر کر کے، آپ ہماری شرائط سے اتفاق کرتے ہیں۔ ادائیگی کے بعد آپ کا ٹکٹ آپ کی ای میل پر بھیج دیا جائے گا۔",
    successTitle: "!رجسٹریشن کی تصدیق ہو گئی",
    successText:
      "آپ کا ٹکٹ آپ کی ای میل پر بھیج دیا گیا ہے۔ براہ کرم اسے محفوظ کریں یا اسکرین شاٹ لیں۔",
    successBack: "ایونٹ پیج پر واپس جائیں",
    cancelledTitle: "ادائیگی منسوخ",
    cancelledText:
      "آپ کی رجسٹریشن مکمل نہیں ہوئی۔ آپ نیچے دوبارہ کوشش کر سکتے ہیں۔",
    shareTitle: "یہ ایونٹ شیئر کریں",
    shareLinkedIn: "لنکڈ ان",
    shareTwitter: "ایکس (ٹویٹر)",
    shareWhatsApp: "واٹس ایپ",
    shareFacebook: "فیس بک",
    shareCopy: "لنک کاپی کریں",
    shareCopied: "!لنک کاپی ہو گیا",
    shareText: "جرمنی میں پاکستانی کمیونٹی کے لیے اننم کے خصوصی بزنس ایونٹ میں شامل ہوں — 24 مئی 2026، ڈارمشٹاڈ کے قریب۔ مل کر سیکھیں، جڑیں اور ترقی کریں!",
    footerText: "اننم ہنفسٹین کی طرف سے منظم",
    footerRights: "جملہ حقوق محفوظ ہیں۔",
  },
};

// ─── Icon Map ───
const iconMap: Record<string, React.ReactNode> = {
  users: <Users className="w-5 h-5" />,
  star: <Star className="w-5 h-5" />,
  home: <Home className="w-5 h-5" />,
  briefcase: <Briefcase className="w-5 h-5" />,
  message: <MessageSquare className="w-5 h-5" />,
  lightbulb: <Lightbulb className="w-5 h-5" />,
  mic: <Mic className="w-5 h-5" />,
  handshake: <Handshake className="w-5 h-5" />,
};

// ─── Countdown Hook ───
function useCountdown(targetDate: Date) {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const interval = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

  const diff = Math.max(0, targetDate.getTime() - now.getTime());
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
  const minutes = Math.floor((diff / (1000 * 60)) % 60);
  const seconds = Math.floor((diff / 1000) % 60);

  return { days, hours, minutes, seconds, isOver: diff === 0 };
}

// ─── Main Component ───
export default function BusinessEvent() {
  const [lang, setLang] = useState<Lang>("en");
  const search = useSearch();
  const [, navigate] = useLocation();

  // Check for cancelled or success params
  const params = useMemo(() => new URLSearchParams(search), [search]);
  const isCancelled = params.get("cancelled") === "true";

  const tx = t[lang];
  const dir = lang === "ur" ? "rtl" : "ltr";

  const eventDate = useMemo(() => new Date("2026-05-24T08:00:00Z"), []);
  const countdown = useCountdown(eventDate);

  // Form state
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [applicationText, setApplicationText] = useState("");

  const STRIPE_PAYMENT_LINK = "https://buy.stripe.com/3cI3cu5LxceN7l37BreUU2e";

  const registerMutation = trpc.event.register.useMutation({
    onSuccess: () => {
      // Registration saved — redirect to Stripe Payment Link
      window.open(STRIPE_PAYMENT_LINK, "_blank", "noopener,noreferrer");
      toast.success(
        lang === "ur"
          ? "رجسٹریشن محفوظ! ادائیگی کے لیے نئی ونڈو کھل رہی ہے..."
          : "Registration saved! Opening payment page..."
      );
    },
    onError: (err) => {
      toast.error(err.message);
    },
  });

  // Social sharing
  const [linkCopied, setLinkCopied] = useState(false);
  const eventUrl = typeof window !== "undefined" ? `${window.location.origin}/event` : "";

  const shareOnLinkedIn = () => {
    const url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(eventUrl)}`;
    window.open(url, "_blank", "noopener,noreferrer,width=600,height=500");
  };

  const shareOnTwitter = () => {
    const text = encodeURIComponent(tx.shareText);
    const url = `https://twitter.com/intent/tweet?text=${text}&url=${encodeURIComponent(eventUrl)}`;
    window.open(url, "_blank", "noopener,noreferrer,width=600,height=500");
  };

  const shareOnWhatsApp = () => {
    const text = encodeURIComponent(`${tx.shareText}\n${eventUrl}`);
    window.open(`https://wa.me/?text=${text}`, "_blank", "noopener,noreferrer");
  };

  const shareOnFacebook = () => {
    const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(eventUrl)}`;
    window.open(url, "_blank", "noopener,noreferrer,width=600,height=500");
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(eventUrl);
      setLinkCopied(true);
      toast.success(tx.shareCopied);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch {
      toast.error("Could not copy link");
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    registerMutation.mutate({
      firstName,
      lastName,
      email,
      phone,
      applicationText,
      language: lang,
    });
  };

  return (
    <div
      dir={dir}
      className="min-h-screen bg-[#0d0b09] text-white"
      style={{ fontFamily: lang === "ur" ? "'Noto Nastaliq Urdu', serif" : "'DM Sans', sans-serif" }}
    >
      {/* Urdu font */}
      {lang === "ur" && (
        <link
          href="https://fonts.googleapis.com/css2?family=Noto+Nastaliq+Urdu:wght@400;700&display=swap"
          rel="stylesheet"
        />
      )}

      {/* ─── Top Bar ─── */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-[#0d0b09]/90 backdrop-blur-md border-b border-[#c9a96e]/20">
        <div className="max-w-7xl mx-auto px-4 h-14 flex items-center justify-between">
          <a href="/" className="text-[#c9a96e] font-semibold text-lg tracking-wide">
            AYU
          </a>
          <button
            onClick={() => setLang(tx.langSwitch)}
            className="flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#c9a96e]/30 text-[#c9a96e] text-sm hover:bg-[#c9a96e]/10 transition-colors"
          >
            <Globe className="w-4 h-4" />
            {tx.langLabel}
          </button>
        </div>
      </div>

      {/* ─── Hero Section ─── */}
      <section className="relative pt-24 pb-16 md:pt-32 md:pb-24 overflow-hidden">
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#1a1510] via-[#0d0b09] to-[#0d0b09]" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[600px] bg-[#c9a96e]/5 rounded-full blur-[120px]" />

        <div className="relative max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Text */}
            <div className={lang === "ur" ? "text-right order-2 md:order-1" : "order-2 md:order-1"}>
              <span className="inline-block px-4 py-1.5 rounded-full bg-[#c9a96e]/10 border border-[#c9a96e]/30 text-[#c9a96e] text-sm font-medium mb-6">
                {tx.heroTag}
              </span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6">
                <span className="text-white">{tx.heroTitle.split(" ")[0]}</span>{" "}
                <span className="text-[#c9a96e]">
                  {tx.heroTitle.split(" ").slice(1).join(" ")}
                </span>
              </h1>
              <p className="text-[#b5a890] text-lg md:text-xl leading-relaxed mb-8 max-w-lg">
                {tx.heroSubtitle}
              </p>

              {/* Event Meta */}
              <div className="flex flex-col gap-3 mb-8">
                <div className="flex items-center gap-3 text-[#e8dcc8]">
                  <Calendar className="w-5 h-5 text-[#c9a96e] shrink-0" />
                  <span>{tx.heroDate}</span>
                </div>
                <div className="flex items-center gap-3 text-[#e8dcc8]">
                  <Clock className="w-5 h-5 text-[#c9a96e] shrink-0" />
                  <span>{tx.heroTime}</span>
                </div>
                <div className="flex items-center gap-3 text-[#e8dcc8]">
                  <MapPin className="w-5 h-5 text-[#c9a96e] shrink-0" />
                  <span>{tx.heroLocation}</span>
                </div>
              </div>

              <a
                href="#register"
                className="inline-flex items-center gap-2 px-8 py-4 bg-[#c9a96e] text-[#0d0b09] font-bold rounded-xl hover:bg-[#d4b87a] transition-colors text-lg"
              >
                {tx.heroCta}
                <ArrowRight className="w-5 h-5" />
              </a>
              <div className="flex items-center gap-4 mt-4">
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#c9a96e]/10 border border-[#c9a96e]/20 text-[#c9a96e] text-xs font-medium uppercase tracking-wider">
                  <Users className="w-3.5 h-3.5" />
                  {tx.formAdultsOnly}
                </span>
                <span className="text-[#7d7060] text-sm italic">
                  {tx.formVibe}
                </span>
              </div>
              <p className="text-[#7d7060] text-sm mt-3">
                {tx.heroSpots}
              </p>
            </div>

            {/* Photo */}
            <div className="order-1 md:order-2 flex justify-center">
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-br from-[#c9a96e]/20 to-transparent rounded-2xl blur-xl" />
                <img
                  src={ANNAM_PHOTO}
                  alt="Annam Hanfstein"
                  className="relative w-full max-w-md rounded-2xl shadow-2xl border border-[#c9a96e]/20 object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Countdown ─── */}
      {!countdown.isOver && (
        <section className="py-12 border-y border-[#c9a96e]/10">
          <div className="max-w-4xl mx-auto px-4">
            <div className="grid grid-cols-4 gap-4 md:gap-8">
              {[
                { value: countdown.days, label: tx.countdownDays },
                { value: countdown.hours, label: tx.countdownHours },
                { value: countdown.minutes, label: tx.countdownMinutes },
                { value: countdown.seconds, label: tx.countdownSeconds },
              ].map((item, i) => (
                <div
                  key={i}
                  className="text-center bg-[#1a1510] rounded-xl p-4 md:p-6 border border-[#c9a96e]/10"
                >
                  <div className="text-3xl md:text-5xl font-bold text-[#c9a96e] tabular-nums">
                    {String(item.value).padStart(2, "0")}
                  </div>
                  <div className="text-[#7d7060] text-xs md:text-sm mt-1 uppercase tracking-wider">
                    {item.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ─── About Section ─── */}
      <section className="py-20 md:py-28">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-6">
            <span className="text-[#c9a96e]">{tx.aboutTitle}</span>
          </h2>
          <p
            className={`text-[#b5a890] text-lg leading-relaxed max-w-3xl mx-auto mb-16 ${
              lang === "ur" ? "text-right" : "text-center"
            }`}
          >
            {tx.aboutText}
          </p>

          {/* Host Card */}
          <div className="max-w-2xl mx-auto bg-[#1a1510] rounded-2xl p-8 border border-[#c9a96e]/15 flex flex-col md:flex-row items-center gap-8">
            <img
              src={ANNAM_PHOTO}
              alt={tx.aboutHostName}
              className="w-32 h-32 rounded-full object-cover border-2 border-[#c9a96e]/30 shrink-0"
            />
            <div className={lang === "ur" ? "text-right" : ""}>
              <p className="text-[#c9a96e] text-sm font-medium uppercase tracking-wider mb-1">
                {tx.aboutHost}
              </p>
              <h3 className="text-2xl font-bold text-white mb-3">
                {tx.aboutHostName}
              </h3>
              <p className="text-[#b5a890] leading-relaxed">
                {tx.aboutHostBio}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Agenda Section ─── */}
      <section className="py-20 md:py-28 bg-[#12100d]">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-16">
            <span className="text-[#c9a96e]">{tx.agendaTitle}</span>
          </h2>

          <div className="space-y-4">
            {tx.agendaItems.map((item, i) => (
              <div
                key={i}
                className={`group flex items-start gap-4 md:gap-6 p-5 md:p-6 rounded-xl bg-[#1a1510] border border-[#c9a96e]/10 hover:border-[#c9a96e]/30 transition-colors ${
                  lang === "ur" ? "flex-row-reverse text-right" : ""
                }`}
              >
                {/* Time */}
                <div className="shrink-0 w-16 text-center">
                  <span className="text-[#c9a96e] font-bold text-lg tabular-nums">
                    {item.time}
                  </span>
                </div>

                {/* Icon */}
                <div className="shrink-0 w-10 h-10 rounded-full bg-[#c9a96e]/10 flex items-center justify-center text-[#c9a96e] group-hover:bg-[#c9a96e]/20 transition-colors">
                  {iconMap[item.icon] || <Sparkles className="w-5 h-5" />}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <h3 className="text-white font-semibold text-lg mb-1">
                    {item.title}
                  </h3>
                  <p className="text-[#7d7060] text-sm">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Registration Form ─── */}
      <section id="register" className="py-20 md:py-28 scroll-mt-20">
        <div className="max-w-2xl mx-auto px-4">
          {/* Cancelled Banner */}
          {isCancelled && (
            <div className="mb-8 p-4 rounded-xl bg-red-900/20 border border-red-500/30 text-center">
              <h3 className="text-red-400 font-semibold mb-1">
                {tx.cancelledTitle}
              </h3>
              <p className="text-red-300/70 text-sm">{tx.cancelledText}</p>
            </div>
          )}

          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold mb-3">
              <span className="text-[#c9a96e]">{tx.formTitle}</span>
            </h2>
            <p className="text-[#7d7060]">{tx.formSubtitle}</p>
          </div>

          <form
            onSubmit={handleSubmit}
            className="bg-[#1a1510] rounded-2xl p-8 border border-[#c9a96e]/15 space-y-5"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-[#b5a890] text-sm mb-1.5">
                  {tx.formFirstName} *
                </label>
                <Input
                  required
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="bg-[#252219] border-[#c9a96e]/15 text-white placeholder:text-[#5a5040] focus:border-[#c9a96e]/50 focus:ring-[#c9a96e]/20"
                  dir="ltr"
                />
              </div>
              <div>
                <label className="block text-[#b5a890] text-sm mb-1.5">
                  {tx.formLastName} *
                </label>
                <Input
                  required
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className="bg-[#252219] border-[#c9a96e]/15 text-white placeholder:text-[#5a5040] focus:border-[#c9a96e]/50 focus:ring-[#c9a96e]/20"
                  dir="ltr"
                />
              </div>
            </div>

            <div>
              <label className="block text-[#b5a890] text-sm mb-1.5">
                {tx.formEmail} *
              </label>
              <Input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-[#252219] border-[#c9a96e]/15 text-white placeholder:text-[#5a5040] focus:border-[#c9a96e]/50 focus:ring-[#c9a96e]/20"
                dir="ltr"
              />
            </div>

            <div>
              <label className="block text-[#b5a890] text-sm mb-1.5">
                {tx.formPhone} *
              </label>
              <Input
                type="tel"
                required
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+49..."
                className="bg-[#252219] border-[#c9a96e]/15 text-white placeholder:text-[#5a5040] focus:border-[#c9a96e]/50 focus:ring-[#c9a96e]/20"
                dir="ltr"
              />
            </div>

            <div>
              <label className="block text-[#b5a890] text-sm mb-1.5">
                {tx.formApplication} *
              </label>
              <Textarea
                required
                minLength={10}
                rows={4}
                value={applicationText}
                onChange={(e) => setApplicationText(e.target.value)}
                placeholder={tx.formApplicationPlaceholder}
                className="bg-[#252219] border-[#c9a96e]/15 text-white placeholder:text-[#5a5040] focus:border-[#c9a96e]/50 focus:ring-[#c9a96e]/20 resize-none"
                dir={dir}
              />
            </div>

            {/* Price + Submit */}
            <div className="pt-4">
              {/* Adults only + Vibe */}
              <div className="flex flex-wrap items-center gap-3 mb-4 pb-4 border-b border-[#c9a96e]/10">
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#c9a96e]/10 border border-[#c9a96e]/20 text-[#c9a96e] text-xs font-medium uppercase tracking-wider">
                  <Users className="w-3.5 h-3.5" />
                  {tx.formAdultsOnly}
                </span>
                <span className="text-[#7d7060] text-sm italic">
                  {tx.formVibe}
                </span>
              </div>
              <div className="flex items-center justify-between mb-4">
                <span className="text-[#7d7060] text-sm">
                  {lang === "ur" ? "ٹکٹ کی قیمت" : "Ticket Price"}
                </span>
                <span className="text-2xl font-bold text-[#c9a96e]">
                  {tx.formPriceCurrency} {tx.formPrice}
                </span>
              </div>
              <Button
                type="submit"
                disabled={registerMutation.isPending}
                className="w-full bg-[#c9a96e] text-[#0d0b09] font-bold py-6 text-lg rounded-xl hover:bg-[#d4b87a] transition-colors disabled:opacity-50"
              >
                {registerMutation.isPending ? (
                  <span className="flex items-center gap-2 justify-center">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    {tx.formProcessing}
                  </span>
                ) : (
                  <span className="flex items-center gap-2 justify-center">
                    {tx.formSubmit}
                    <ArrowRight className="w-5 h-5" />
                  </span>
                )}
              </Button>
              <p className="text-[#5a5040] text-xs text-center mt-3">
                {tx.formDisclaimer}
              </p>
            </div>
          </form>
        </div>
      </section>

      {/* ─── Social Sharing ─── */}
      <section className="py-16 md:py-20">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <div className="flex items-center justify-center gap-3 mb-8">
            <Share2 className="w-5 h-5 text-[#c9a96e]" />
            <h2 className="text-2xl md:text-3xl font-bold text-[#c9a96e]">
              {tx.shareTitle}
            </h2>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-3">
            {/* LinkedIn */}
            <button
              onClick={shareOnLinkedIn}
              className="group flex items-center gap-2.5 px-5 py-3 rounded-xl bg-[#1a1510] border border-[#c9a96e]/15 hover:border-[#0A66C2]/50 hover:bg-[#0A66C2]/10 transition-all duration-200"
            >
              <svg className="w-5 h-5 text-[#b5a890] group-hover:text-[#0A66C2] transition-colors" viewBox="0 0 24 24" fill="currentColor">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
              </svg>
              <span className="text-sm font-medium text-[#b5a890] group-hover:text-white transition-colors">
                {tx.shareLinkedIn}
              </span>
            </button>

            {/* Twitter / X */}
            <button
              onClick={shareOnTwitter}
              className="group flex items-center gap-2.5 px-5 py-3 rounded-xl bg-[#1a1510] border border-[#c9a96e]/15 hover:border-white/30 hover:bg-white/5 transition-all duration-200"
            >
              <svg className="w-5 h-5 text-[#b5a890] group-hover:text-white transition-colors" viewBox="0 0 24 24" fill="currentColor">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
              </svg>
              <span className="text-sm font-medium text-[#b5a890] group-hover:text-white transition-colors">
                {tx.shareTwitter}
              </span>
            </button>

            {/* WhatsApp */}
            <button
              onClick={shareOnWhatsApp}
              className="group flex items-center gap-2.5 px-5 py-3 rounded-xl bg-[#1a1510] border border-[#c9a96e]/15 hover:border-[#25D366]/50 hover:bg-[#25D366]/10 transition-all duration-200"
            >
              <svg className="w-5 h-5 text-[#b5a890] group-hover:text-[#25D366] transition-colors" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              <span className="text-sm font-medium text-[#b5a890] group-hover:text-white transition-colors">
                {tx.shareWhatsApp}
              </span>
            </button>

            {/* Facebook */}
            <button
              onClick={shareOnFacebook}
              className="group flex items-center gap-2.5 px-5 py-3 rounded-xl bg-[#1a1510] border border-[#c9a96e]/15 hover:border-[#1877F2]/50 hover:bg-[#1877F2]/10 transition-all duration-200"
            >
              <svg className="w-5 h-5 text-[#b5a890] group-hover:text-[#1877F2] transition-colors" viewBox="0 0 24 24" fill="currentColor">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
              <span className="text-sm font-medium text-[#b5a890] group-hover:text-white transition-colors">
                {tx.shareFacebook}
              </span>
            </button>

            {/* Copy Link */}
            <button
              onClick={copyLink}
              className={`group flex items-center gap-2.5 px-5 py-3 rounded-xl border transition-all duration-200 ${
                linkCopied
                  ? "bg-[#c9a96e]/15 border-[#c9a96e]/40"
                  : "bg-[#1a1510] border-[#c9a96e]/15 hover:border-[#c9a96e]/40 hover:bg-[#c9a96e]/10"
              }`}
            >
              {linkCopied ? (
                <Check className="w-5 h-5 text-[#c9a96e]" />
              ) : (
                <Link className="w-5 h-5 text-[#b5a890] group-hover:text-[#c9a96e] transition-colors" />
              )}
              <span className={`text-sm font-medium transition-colors ${
                linkCopied ? "text-[#c9a96e]" : "text-[#b5a890] group-hover:text-white"
              }`}>
                {linkCopied ? tx.shareCopied : tx.shareCopy}
              </span>
            </button>
          </div>
        </div>
      </section>

      {/* ─── Footer ─── */}
      <footer className="py-8 border-t border-[#c9a96e]/10">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <p className="text-[#7d7060] text-sm">
            {tx.footerText} &middot; {tx.footerRights}
          </p>
        </div>
      </footer>
    </div>
  );
}
